/**
 * Dashboard hooks for Admin TMA
 * Provides stats and feed data fetching
 */

import { useQuery } from '@tanstack/react-query';
import { fetchStats, fetchFeed, type DashboardStats, type FeedResponse } from '@/lib/api';

// Query keys
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  feed: (params?: { limit?: number; offset?: number }) =>
    [...dashboardKeys.all, 'feed', params] as const,
};

/**
 * Hook to fetch dashboard KPI statistics
 * Refreshes every 30 seconds
 */
export function useStats() {
  return useQuery<DashboardStats, Error>({
    queryKey: dashboardKeys.stats(),
    queryFn: fetchStats,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

/**
 * Hook to fetch event feed
 */
export function useFeed(params?: { limit?: number; offset?: number }) {
  return useQuery<FeedResponse, Error>({
    queryKey: dashboardKeys.feed(params),
    queryFn: () => fetchFeed(params),
    staleTime: 15_000,
  });
}

// Event action labels in Russian
export const EVENT_ACTION_LABELS: Record<string, string> = {
  MASTER_STATUS_CHANGED: 'Статус мастера изменен',
  CONTACTS_REVEALED: 'Контакты раскрыты',
  ORDER_DISPUTED: 'Заказ оспорен',
  ORDER_CREATED: 'Заказ создан',
  ORDER_STATUS_CHANGED: 'Статус заказа изменен',
  PAYMENT_RECEIVED: 'Оплата получена',
  PAYMENT_REFUNDED: 'Оплата возвращена',
  BID_PLACED: 'Ставка размещена',
};

// Event action icons (for UI)
export const EVENT_ACTION_ICONS: Record<string, string> = {
  MASTER_STATUS_CHANGED: 'user-check',
  CONTACTS_REVEALED: 'phone',
  ORDER_DISPUTED: 'alert-triangle',
  ORDER_CREATED: 'plus-circle',
  ORDER_STATUS_CHANGED: 'refresh-cw',
  PAYMENT_RECEIVED: 'credit-card',
  PAYMENT_REFUNDED: 'rotate-ccw',
  BID_PLACED: 'dollar-sign',
};

// Event action colors
export const EVENT_ACTION_COLORS: Record<string, string> = {
  MASTER_STATUS_CHANGED: 'bg-blue-500',
  CONTACTS_REVEALED: 'bg-green-500',
  ORDER_DISPUTED: 'bg-red-500',
  ORDER_CREATED: 'bg-blue-500',
  ORDER_STATUS_CHANGED: 'bg-orange-500',
  PAYMENT_RECEIVED: 'bg-green-500',
  PAYMENT_REFUNDED: 'bg-amber-500',
  BID_PLACED: 'bg-purple-500',
};

/**
 * Format money value
 */
export function formatMoney(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0 ₽';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * Format relative time
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'только что';
  if (diffMins < 5) return `${diffMins} мин назад`;
  if (diffMins < 60) return `${diffMins} мин назад`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} ч назад`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) return `${diffDays} дн назад`;

  return date.toLocaleDateString('ru-RU');
}
